import {FormControlLabel, Switch} from "@mui/material";

// The design draws a pill with the thumb inside it, where MUI's small Switch is a 10px track with a
// 16px thumb on top, so these sizes replace it rather than tune it. Padding is what sizes the track,
// as the track fills whatever the root's padding leaves behind.
const TRACK_WIDTH_PX = 36;
const TRACK_HEIGHT_PX = 20;
const THUMB_SIZE_PX = 16;

/**
 * Padding around the track, which is what gives the switch a finger-sized hit area without growing the
 * pill it draws. Exported because rows next to the switch have to allow for it in their own spacing.
 */
export const SWITCH_HIT_AREA_PADDING_PX = 6;

/** Overall height of the switch, which the sheet has to allow for when sizing the row it sits in. */
export const SWITCH_HEIGHT_PX = TRACK_HEIGHT_PX + (2 * SWITCH_HIT_AREA_PADDING_PX);

/** Gap between the thumb and the edge of the track, which centres the thumb across the track's height. */
const THUMB_INSET_PX = (TRACK_HEIGHT_PX - THUMB_SIZE_PX) / 2;

/** How far the thumb slides when switched on: the track's width, less the thumb and its two insets. */
const THUMB_TRAVEL_PX = TRACK_WIDTH_PX - THUMB_SIZE_PX - (2 * THUMB_INSET_PX);

const SWITCH_SX = {
    width: TRACK_WIDTH_PX + (2 * SWITCH_HIT_AREA_PADDING_PX),
    height: TRACK_HEIGHT_PX + (2 * SWITCH_HIT_AREA_PADDING_PX),
    padding: `${SWITCH_HIT_AREA_PADDING_PX}px`,
    "& .MuiSwitch-switchBase": {
        padding: `${SWITCH_HIT_AREA_PADDING_PX + THUMB_INSET_PX}px`,
        "&.Mui-checked": {
            transform: `translateX(${THUMB_TRAVEL_PX}px)`,
            color: "common.white",
            "& + .MuiSwitch-track": {backgroundColor: "primary.main", opacity: 1}
        }
    },
    "& .MuiSwitch-thumb": {
        width: THUMB_SIZE_PX,
        height: THUMB_SIZE_PX,
        // MUI's lift shadow only muddies the pill's edge.
        boxShadow: "none"
    },
    "& .MuiSwitch-track": {borderRadius: TRACK_HEIGHT_PX / 2}
};

export interface LabeledSwitchProps {
    label: string;
    checked: boolean;
    setChecked: (checked: boolean) => void;
}

/** A labeled on/off switch drawn to the design's proportions rather than MUI's. */
export function LabeledSwitch(props: LabeledSwitchProps) {
    const {label, checked, setChecked} = props;

    return <FormControlLabel
        // Replaces FormControlLabel's default -11px pull with the switch's own padding, which leaves the
        // track flush with the gutter.
        sx={{ml: `-${SWITCH_HIT_AREA_PADDING_PX}px`, mr: 0}}
        control={<Switch sx={SWITCH_SX} checked={checked}
                         onChange={(event) => setChecked(event.target.checked)}/>}
        label={label}
        slotProps={{typography: {variant: "body1"}}}
    />;
}
